import { Modal } from './ui';
import { Tag, Calendar, DollarSign, Percent, Truck } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { ActivePromosModalProps, PromoCode } from '../types';

export function ActivePromosModal({ isOpen, onClose }: ActivePromosModalProps) {
  const { promoCodes } = useUser();
  const promoCodesArray = Object.values(promoCodes).filter(promo => promo.active);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getPromoIcon = (promoType: string | null) => {
    switch (promoType) {
      case 'PERCENTAGE': return <Percent className="w-5 h-5" />;
      case 'DOLLAR':     return <DollarSign className="w-5 h-5" />;
      case 'FREESHIP':   return <Truck className="w-5 h-5" />;
      case 'ITEM':       return <Tag className="w-5 h-5" />;
      default:           return <Tag className="w-5 h-5" />;
    }
  };

  const getTypeLabel = (promoType: string | null) => {
    switch (promoType) {
      case 'DOLLAR':     return 'Dollar';
      case 'PERCENTAGE': return 'Percentage';
      case 'FREESHIP':   return 'Free Shipping';
      case 'ITEM':       return 'Item';
      default:           return promoType ?? 'Promo';
    }
  };

  const getValueBadge = (promo: PromoCode) => {
    switch (promo.promoType) {
      case 'DOLLAR':
        return promo.balance > 0 ? `$${promo.balance.toFixed(2)} Balance` : null;
      case 'ITEM':
        return promo.balance > 0 ? `${Math.floor(promo.balance)} item${Math.floor(promo.balance) !== 1 ? 's' : ''} remaining` : null;
      case 'PERCENTAGE': {
        const pct = promo.percent || Math.round(promo.balance);
        return pct ? `${pct}% Off` : null;
      }
      case 'FREESHIP':
        return 'Free Shipping';
      default:
        return promo.balance > 0 ? `$${promo.balance.toFixed(2)} Balance` : null;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Active Promotions" size="lg">
      <div className="px-6 py-4">
        {promoCodesArray.length === 0 ? (
          <div className="text-center py-12">
            <Tag className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Promotions</h3>
            <p className="text-gray-500">There are currently no promotions available for your account.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {promoCodesArray.map((promo) => {
              const valueBadge = getValueBadge(promo);
              const startFormatted = formatDate(promo.startDate);
              const endFormatted = formatDate(promo.endDate);

              return (
                <div
                  key={promo.id}
                  className="border border-gray-200 rounded-xl p-4"
                >
                  {/* Top row: icon + name + badges */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className="text-blue-600 mt-0.5 flex-shrink-0">
                        {getPromoIcon(promo.promoType)}
                      </div>
                      <div>
                        <h3 className="text-base font-bold text-gray-900 leading-tight">
                          {promo.promo || 'Promotion'}
                        </h3>
                        {promo.autoapply && (
                          <span className="text-sm font-semibold text-green-600">
                            Auto-applied
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex-shrink-0 flex items-center gap-2">
                      {promo.promoType && (
                        <span className="bg-gray-100 text-gray-600 text-xs font-semibold px-3 py-1 rounded-full tracking-wide">
                          {getTypeLabel(promo.promoType)}
                        </span>
                      )}
                      {valueBadge && (
                        <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full tracking-wide">
                          {valueBadge}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  {promo.memo && (
                    <p className="mt-2 text-sm text-gray-600 pl-8">{promo.memo}</p>
                  )}

                  {/* Date range */}
                  {startFormatted && endFormatted && (
                    <div className="mt-2 pl-8 flex items-center gap-1.5 text-sm text-gray-500">
                      <Calendar className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                      <span>{startFormatted} - {endFormatted}</span>
                    </div>
                  )}

                  {/* Restrictions */}
                  {!!((promo.categories?.length ?? 0) > 0 || (promo.items?.length ?? 0) > 0 || promo.itemLimit || promo.creditcardonly || promo.excludeShipping) && (
                    <details className="mt-3 pl-8">
                      <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600">
                        Restrictions & Details
                      </summary>
                      <div className="mt-1.5 text-xs text-gray-500 space-y-2">
                        {(promo.categories?.length ?? 0) > 0 && (
                          <div>
                            <p className="font-medium text-gray-600 mb-1">Applies to categories:</p>
                            <div className="flex flex-wrap gap-1">
                              {promo.categories!.map(cat => (
                                <span key={cat.id} className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">
                                  {cat.name}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {(promo.items?.length ?? 0) > 0 && (
                          <div>
                            <p className="font-medium text-gray-600 mb-1">Applies to specific items:</p>
                            <div className="flex flex-wrap gap-1">
                              {promo.items!.map(item => (
                                <span key={item.id} className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">
                                  {item.sku ? `${item.item} (${item.sku})` : item.item}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {promo.itemLimit ? <p>• Limited to {promo.itemLimit} items</p> : null}
                        {promo.creditcardonly ? <p>• Credit card payment required</p> : null}
                        {promo.excludeShipping ? <p>• Excludes shipping costs</p> : null}
                      </div>
                    </details>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
}
